/**
 * Shop the Look — Storefront Widget
 * ─────────────────────────────────
 * INSTALLATION (Shopify):
 *   1. Go to Online Store → Themes → Edit code
 *   2. Under Assets, click "Add a new asset" → create blank file → name it "shop-the-look-widget.js"
 *   3. Paste this entire file's contents there
 *   4. In your theme.liquid (or a Custom HTML section), add your lookbook HTML
 *      (generated by the Admin app) — see embed code example.
 *
 * USAGE:
 *   <div class="stl-lookbook"
 *        data-image="https://cdn.shopify.com/s/files/1/your-image.jpg"
 *        data-hotspots='[{"id":1,"xPct":34.5,"yPct":22.1,"label":"Silk blouse","url":"/products/silk-blouse","price":"$89.00"}]'>
 *   </div>
 *   <script src="{{ 'shop-the-look-widget.js' | asset_url }}" defer></script>
 *
 * You can place multiple .stl-lookbook divs on the same page.
 */

(function () {
  'use strict';

  const STYLES = `
    .stl-lookbook {
      position: relative;
      display: inline-block;
      line-height: 0;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
    }
    .stl-lookbook img.stl-image {
      display: block;
      width: 100%;
      height: auto;
      border-radius: 8px;
    }
    .stl-hotspot {
      position: absolute;
      width: 30px;
      height: 30px;
      transform: translate(-50%, -50%);
      cursor: pointer;
      z-index: 10;
    }
    .stl-hotspot-ring {
      position: absolute;
      inset: 0;
      border-radius: 50%;
      background: rgba(192, 132, 252, 0.35);
      animation: stl-pulse 2.2s ease-in-out infinite;
    }
    .stl-hotspot-dot {
      position: absolute;
      top: 50%; left: 50%;
      width: 18px; height: 18px;
      transform: translate(-50%, -50%);
      border-radius: 50%;
      background: #c084fc;
      border: 2.5px solid #fff;
      box-shadow: 0 1px 4px rgba(0,0,0,0.25);
      display: flex; align-items: center; justify-content: center;
      transition: transform 0.15s, background 0.15s;
    }
    .stl-hotspot-dot svg {
      width: 9px; height: 9px; fill: none;
      stroke: #fff; stroke-width: 2.5; stroke-linecap: round; stroke-linejoin: round;
    }
    .stl-hotspot:hover .stl-hotspot-dot,
    .stl-hotspot.active .stl-hotspot-dot {
      transform: translate(-50%, -50%) scale(1.2);
      background: #a855f7;
    }
    @keyframes stl-pulse {
      0%, 100% { transform: scale(1); opacity: 0.8; }
      50%       { transform: scale(1.7); opacity: 0; }
    }

    /* Tooltip / popup card */
    .stl-popup {
      position: absolute;
      z-index: 100;
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 4px 24px rgba(0,0,0,0.15), 0 1px 4px rgba(0,0,0,0.08);
      padding: 14px 16px;
      min-width: 200px;
      max-width: 240px;
      pointer-events: none;
      opacity: 0;
      transform: translateY(6px) scale(0.97);
      transition: opacity 0.18s ease, transform 0.18s ease;
      border: 1px solid rgba(0,0,0,0.07);
    }
    .stl-popup.visible {
      opacity: 1;
      transform: translateY(0) scale(1);
      pointer-events: auto;
    }
    .stl-popup-label {
      font-size: 13px;
      font-weight: 600;
      color: #1a1a2e;
      margin-bottom: 4px;
      line-height: 1.3;
    }
    .stl-popup-price {
      font-size: 12px;
      color: #7c3aed;
      font-weight: 500;
      margin-bottom: 12px;
    }
    .stl-popup-btn {
      display: block;
      text-align: center;
      background: #1a1a2e;
      color: #fff !important;
      text-decoration: none !important;
      font-size: 12px;
      font-weight: 600;
      padding: 8px 14px;
      border-radius: 7px;
      transition: background 0.15s;
      letter-spacing: 0.2px;
    }
    .stl-popup-btn:hover { background: #2d2d4e !important; color: #fff !important; }
    .stl-popup-arrow {
      position: absolute;
      width: 10px; height: 10px;
      background: #fff;
      border-left: 1px solid rgba(0,0,0,0.07);
      border-top: 1px solid rgba(0,0,0,0.07);
    }
    .stl-popup-arrow.arrow-bottom {
      bottom: -5px;
      left: 50%; transform: translateX(-50%) rotate(225deg);
    }
    .stl-popup-arrow.arrow-top {
      top: -5px;
      left: 50%; transform: translateX(-50%) rotate(45deg);
    }
  `;

  function injectStyles() {
    if (document.getElementById('stl-styles')) return;
    const style = document.createElement('style');
    style.id = 'stl-styles';
    style.textContent = STYLES;
    document.head.appendChild(style);
  }

  function buildLookbook(container) {
    const imageSrc = container.dataset.image;
    let hotspots = [];
    try { hotspots = JSON.parse(container.dataset.hotspots || '[]'); } catch (e) {}

    if (!imageSrc) { console.warn('[ShopTheLook] Missing data-image on', container); return; }

    container.style.position = 'relative';

    const img = document.createElement('img');
    img.src = imageSrc;
    img.alt = 'Shop the look';
    img.className = 'stl-image';
    img.loading = 'lazy';
    container.appendChild(img);

    let activePopup = null;
    let activeHotspot = null;

    hotspots.forEach(function (h, index) {
      const dot = document.createElement('div');
      dot.className = 'stl-hotspot';
      dot.style.left = h.xPct + '%';
      dot.style.top = h.yPct + '%';
      dot.setAttribute('aria-label', 'View product: ' + (h.label || 'Item ' + (index + 1)));
      dot.setAttribute('role', 'button');
      dot.setAttribute('tabindex', '0');
      dot.innerHTML = `
        <div class="stl-hotspot-ring"></div>
        <div class="stl-hotspot-dot">
          <svg viewBox="0 0 12 12"><line x1="6" y1="2" x2="6" y2="10"/><line x1="2" y1="6" x2="10" y2="6"/></svg>
        </div>`;

      const popup = document.createElement('div');
      popup.className = 'stl-popup';
      popup.innerHTML = `
        <div class="stl-popup-arrow arrow-bottom" id="arrow-${h.id}"></div>
        <div class="stl-popup-label">${escapeHTML(h.label || 'View product')}</div>
        ${h.price ? '<div class="stl-popup-price">' + escapeHTML(h.price) + '</div>' : ''}
        <a class="stl-popup-btn" href="${escapeAttr(h.url)}" ${isExternalUrl(h.url) ? 'target="_blank" rel="noopener noreferrer"' : ''}>Shop now →</a>
      `;

      function showPopup() {
        if (activePopup && activePopup !== popup) {
          activePopup.classList.remove('visible');
          if (activeHotspot) activeHotspot.classList.remove('active');
        }
        positionPopup(popup, dot, container, h);
        popup.classList.add('visible');
        dot.classList.add('active');
        activePopup = popup;
        activeHotspot = dot;
      }

      function hidePopup() {
        popup.classList.remove('visible');
        dot.classList.remove('active');
        if (activePopup === popup) { activePopup = null; activeHotspot = null; }
      }

      // Hover on desktop
      dot.addEventListener('mouseenter', showPopup);
      dot.addEventListener('mouseleave', function (e) {
        // Keep open if mouse moves into popup
        setTimeout(function () {
          if (!popup.matches(':hover') && !dot.matches(':hover')) hidePopup();
        }, 80);
      });
      popup.addEventListener('mouseleave', function () {
        if (!dot.matches(':hover')) hidePopup();
      });

      // Click/tap for mobile
      dot.addEventListener('click', function (e) {
        e.stopPropagation();
        if (popup.classList.contains('visible')) { hidePopup(); } else { showPopup(); }
      });

      // Keyboard
      dot.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); showPopup(); }
        if (e.key === 'Escape') hidePopup();
      });

      container.appendChild(dot);
      container.appendChild(popup);
    });

    // Close on outside click
    document.addEventListener('click', function () {
      if (activePopup) {
        activePopup.classList.remove('visible');
        if (activeHotspot) activeHotspot.classList.remove('active');
        activePopup = null; activeHotspot = null;
      }
    });
  }

  function positionPopup(popup, dot, container, h) {
    popup.style.opacity = '0';
    popup.style.display = 'block';

    const dotLeft = h.xPct / 100;
    const dotTop = h.yPct / 100;
    const pw = popup.offsetWidth || 220;
    const ph = popup.offsetHeight || 100;
    const cw = container.offsetWidth;
    const ch = container.offsetHeight;

    let left = dotLeft * cw - pw / 2;
    let showAbove = dotTop > 0.55;
    let top = showAbove
      ? dotTop * ch - ph - 20
      : dotTop * ch + 24;

    // Clamp horizontally
    left = Math.max(8, Math.min(left, cw - pw - 8));

    popup.style.left = left + 'px';
    popup.style.top = top + 'px';

    const arrow = popup.querySelector('[id^="arrow-"]');
    if (arrow) {
      arrow.className = 'stl-popup-arrow ' + (showAbove ? 'arrow-bottom' : 'arrow-top');
      const arrowLeft = (dotLeft * cw) - left;
      arrow.style.left = Math.max(16, Math.min(arrowLeft, pw - 16)) + 'px';
      arrow.style.transform = 'translateX(-50%) rotate(' + (showAbove ? '225deg' : '45deg') + ')';
    }

    popup.style.opacity = '';
  }

  function escapeHTML(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function escapeAttr(str) {
    return String(str).replace(/"/g, '&quot;');
  }
  function isExternalUrl(url) {
    return /^https?:\/\//i.test(url) && !url.includes(window.location.hostname);
  }

  function init() {
    injectStyles();
    document.querySelectorAll('.stl-lookbook').forEach(function (el) {
      if (!el.dataset.stlInit) {
        el.dataset.stlInit = '1';
        buildLookbook(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Support Shopify section re-renders (theme editor)
  document.addEventListener('shopify:section:load', init);
})();
